import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { SetPassword_setPassword_errors } from "@saleor/auth/types/SetPassword";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import getAccountErrorMessage from "@saleor/utils/errors/account";
import {
  hasNoError,
  useValidation
} from "@saleor/utils/validators/resetPassword";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
const useStyles = makeStyles(
  theme => ({
    actionWrapper: {
      alignItems: "center",
      display: "flex",
      justifyContent: "flex-end"
    },
    errorText: {
      color: theme.palette.error.contrastText
    },
    link: {
      color: theme.palette.primary.main,
      textDecoration: "none"
    },
    panel: {
      "& .MuiAlert-icon": {
        color: "#FFFFFF"
      },
      "& .MuiPaper-root": {
        background: theme.palette.error.main,
        borderRadius: 0,
        color: "#FFFFFF"
      },
      "& .MuiTypography-caption": {
        fontSize: "0.875rem !important"
      },
      background: "#B20B0B",
      borderRadius: 0,
      left: 0,
      marginBottom: theme.spacing(3),
      marginTop: "-40px",
      padding: "10px 15px",
      position: "absolute",
      right: 0,
      top: 0
    },
    submit: {
      marginLeft: "1rem",
      width: "50%"
    }
  }),
  {
    name: "NewPasswordPage"
  }
);

export interface NewPasswordPageFormData {
  password: string;
  confirmPassword: string;
}
export interface NewPasswordPageProps {
  disabled: boolean;
  errors: SetPassword_setPassword_errors[];
  onSubmit: (data: NewPasswordPageFormData) => void;
}

const initialForm: NewPasswordPageFormData = {
  confirmPassword: "",
  password: ""
};

const NewPasswordPage: React.FC<NewPasswordPageProps> = props => {
  const { disabled, errors, onSubmit } = props;
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const classes = useStyles(props);
  const intl = useIntl();
  const error = getAccountErrorMessage(
    errors.find(err => err.field === "password"),
    intl
  );
  const initalFormValidation = {
    confirmPassword: null,
    password: null
  };
  const [clientError, setErrorValdiation] = useState(initalFormValidation);

  const submitForm = event => {
    const validate = useValidation(event);
    setErrorValdiation(validate);
    if (hasNoError(validate)) {
      onSubmit(event);
    }
  };
  return (
    <Form initial={initialForm} onSubmit={submitForm}>
      {({ change: handleChange, data, submit: handleSubmit }) => {
        const passwordError =
          data.password &&
          data.confirmPassword &&
          data.password !== data.confirmPassword;

        return (
          <>
            {clientError.password && clientError.confirmPassword ? (
              <div className={classes.panel}>
                <Typography variant="caption" className={classes.errorText}>
                  {clientError.password}
                </Typography>
              </div>
            ) : error ? (
              <div className={classes.panel}>
                <Typography variant="caption" className={classes.errorText}>
                  {error}
                </Typography>
              </div>
            ) : (
              <></>
            )}
            <Typography>
              <FormattedMessage defaultMessage="Please set up a new password." />
            </Typography>
            <FormSpacer />
            <TextField
              autoFocus
              fullWidth
              autoComplete="none"
              disabled={disabled}
              label={intl.formatMessage({
                defaultMessage: "New Password"
              })}
              name="password"
              type={showNewPassword ? "text" : "password"}
              onChange={handleChange}
              value={data.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <FormSpacer />
            <TextField
              fullWidth
              error={!!passwordError}
              autoComplete="none"
              disabled={disabled}
              label={intl.formatMessage({
                defaultMessage: "Confirm Password"
              })}
              name="confirmPassword"
              onChange={handleChange}
              type={showConfirmPassword ? "text" : "password"}
              value={data.confirmPassword}
              helperText={
                passwordError &&
                intl.formatMessage({
                  defaultMessage: "Passwords do not match"
                })
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <FormSpacer />
            <div className={classes.actionWrapper}>
              <Link to={"/login"} className={classes.link}>
                Login
              </Link>
              <Button
                className={classes.submit}
                color="primary"
                disabled={
                  (passwordError && data.password.length > 0) || disabled
                }
                variant="contained"
                onClick={handleSubmit}
                type="submit"
              >
                <FormattedMessage
                  defaultMessage="Set new password"
                  description="button"
                />
              </Button>
            </div>
          </>
        );
      }}
    </Form>
  );
};

NewPasswordPage.displayName = "NewPasswordPage";
export default NewPasswordPage;
